// Collection actions and requests have one owner, including after facet renders.
const REQUEST_TIMEOUT_MS = 30000;
const CATEGORY_DESKTOP_MIN_WIDTH = 768;
const CATEGORY_HIDE_SCROLL_Y = 65;
const EMPTY_TOOLBAR_SCROLL_Y = 50;
const CATEGORY_DRAG_THRESHOLD_PX = 6;

class CollectionView extends HTMLElement {
  #listeners;
  #navigationListeners;
  #request;
  #loading = false;
  #queuedLayout;
  #layoutWriteController;
  #writingLayout = false;
  #desktopLayout;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    this.addEventListener("click", (event) => this.#onClick(event), {
      signal: this.#listeners.signal,
    });
    this.#configureNavigation();
    this.#syncControls();
    this.#notifyMediaView();
    this.#applyDefaultSort();
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#navigationListeners?.abort();
    if (this.#request) clearTimeout(this.#request.timeout);
    this.#request?.controller.abort();
    this.#request = undefined;
    this.#queuedLayout = undefined;
    this.#layoutWriteController?.abort();
    this.#setLoading(false);
  }

  // Called by the native facet dispatcher, including drawers moved to body.
  async update(value) {
    const request = this.#startRequest();
    try {
      const { url, view } = await this.#fetchSection(value, request);
      if (!this.#isCurrentRequest(request)) return;
      const list = this.#productList;
      const desktopLayout = list?.getAttribute("collection-desktop-layout") || this.#desktopLayout;
      const mediaMode = this.dataset.productMediaMode;
      const focusedId = document.activeElement?.id;
      const openInputNames = new Set(Array.from(
        this.querySelectorAll('facets-form details[open] input[name*="filter."]'),
        (input) => input.name
      ));
      view.querySelectorAll("facets-form details").forEach((details) => {
        details.open = Array.from(details.querySelectorAll('input[name*="filter."]'))
          .some((input) => openInputNames.has(input.name));
      });

      this.#navigationListeners?.abort();
      this.replaceChildren(...document.importNode(view, true).childNodes);
      for (const attribute of view.attributes) {
        if (attribute.name.startsWith("data-")) this.setAttribute(attribute.name, attribute.value);
      }
      if (mediaMode) this.dataset.productMediaMode = mediaMode;
      if (desktopLayout) this.#desktopLayout = desktopLayout;
      this.#configureNavigation();
      this.#syncControls();
      this.#notifyMediaView();
      this.#commitUrl(url);
      if (focusedId) document.getElementById(focusedId)?.focus();
      this.querySelector(".collection")?.scrollIntoView({
        block: "start",
        behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      });
    } catch (error) {
      if (this.#isCurrentRequest(request)) this.#showRequestError();
    } finally {
      this.#finishRequest(request);
    }
  }

  get #productList() {
    return this.querySelector(".collection__main product-list");
  }

  #onClick(event) {
    if (!(event.target instanceof Element)) return;
    const control = event.target.closest("[data-collection-action]");
    if (!control || !this.contains(control)) return;
    if (control.dataset.collectionAction === "more" &&
      (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey || (event.button !== undefined && event.button !== 0))) return;
    event.preventDefault();
    switch (control.dataset.collectionAction) {
      case "layout": this.#toggleLayout(); break;
      case "media": this.#toggleMedia(); break;
      case "more":
        if (!this.#loading) void this.#loadMore(control);
        break;
    }
  }

  #toggleLayout() {
    const list = this.#productList;
    if (!list) return;
    const layout = list.getAttribute("collection-desktop-layout") === "large" ? "medium" : "large";
    this.#desktopLayout = layout;
    this.#syncControls();
    this.#queuedLayout = layout;
    void this.#persistLayout();
  }

  #toggleMedia() {
    this.dataset.productMediaMode = this.dataset.productMediaMode === "secondary" ? "primary" : "secondary";
    this.#syncControls();
    this.#notifyMediaView();
  }

  #syncControls() {
    const list = this.#productList;
    this.#desktopLayout ||= list?.getAttribute("collection-desktop-layout");
    if (list && this.#desktopLayout) list.setAttribute("collection-desktop-layout", this.#desktopLayout);
    const layoutButton = this.querySelector('[data-collection-action="layout"]');
    const isLarge = this.#desktopLayout === "large";
    layoutButton?.classList.toggle("is-active", isLarge);
    layoutButton?.setAttribute("aria-pressed", String(isLarge));
    this.#productList?.style.removeProperty("grid-template-columns");
    const mediaButton = this.querySelector('[data-collection-action="media"]');
    if (mediaButton) {
      const secondary = this.dataset.productMediaMode === "secondary";
      mediaButton.textContent = secondary ? this.dataset.secondaryLabel : this.dataset.primaryLabel;
      mediaButton.setAttribute("aria-pressed", String(secondary));
    }
  }

  #notifyMediaView() {
    this.dispatchEvent(new CustomEvent("product-card:media-view", {
      bubbles: true,
      detail: { mode: this.dataset.productMediaMode === "secondary" ? "secondary" : "primary" },
    }));
  }

  #startRequest() {
    if (this.#request) clearTimeout(this.#request.timeout);
    this.#request?.controller.abort();
    const controller = new AbortController();
    const request = {
      controller,
      timeout: setTimeout(() => controller.abort(new DOMException("Request timed out", "TimeoutError")), REQUEST_TIMEOUT_MS),
    };
    this.#request = request;
    const status = this.querySelector("[data-collection-status]");
    if (status) { status.hidden = true; status.textContent = ""; }
    this.#setLoading(true);
    return request;
  }

  #isCurrentRequest(request) {
    return this.isConnected && this.#request === request;
  }

  #finishRequest(request) {
    clearTimeout(request.timeout);
    if (this.#request !== request) return;
    this.#request = undefined;
    this.#setLoading(false);
  }

  #setLoading(loading) {
    if (this.#loading === loading) return;
    this.#loading = loading;
    this.setAttribute("aria-busy", String(loading));
    const more = this.querySelector('[data-collection-action="more"]');
    if (more) {
      more.setAttribute("aria-disabled", String(loading));
      if (loading) {
        more.dataset.restingLabel = more.textContent;
        more.textContent = this.dataset.loadingLabel;
      } else if (more.dataset.restingLabel) {
        more.textContent = more.dataset.restingLabel;
        delete more.dataset.restingLabel;
      }
    }
    document.documentElement.dispatchEvent(new CustomEvent(
      loading ? "theme:loading:start" : "theme:loading:end", { bubbles: true }
    ));
  }

  #requestUrl(value) {
    const url = new URL(value, location.href);
    const currentUrl = new URL(location.href);
    if (url.origin !== currentUrl.origin || url.pathname !== currentUrl.pathname) {
      throw new Error("Collection URL does not match the current collection");
    }
    const sectionId = this.dataset.sectionId;
    if (!sectionId) throw new Error("Collection section ID is missing");
    url.searchParams.set("section_id", sectionId);
    // Shopify theme previews must remain on the selected draft during AJAX.
    const previewThemeId = currentUrl.searchParams.get("preview_theme_id");
    if (previewThemeId) url.searchParams.set("preview_theme_id", previewThemeId);
    return url;
  }

  async #fetchSection(value, request) {
    const url = this.#requestUrl(value);
    const response = await fetch(url, { signal: request.controller.signal, credentials: "same-origin" });
    if (!response.ok) throw new Error("Collection request failed");
    const parsed = new DOMParser().parseFromString(await response.text(), "text/html");
    if (!this.#isCurrentRequest(request) || request.controller.signal.aborted) {
      throw new DOMException("Collection request cancelled", "AbortError");
    }
    const section = parsed.getElementById(`shopify-section-${this.dataset.sectionId}`);
    const view = section?.querySelector("collection-view");
    if (!view || view.dataset.sectionId !== this.dataset.sectionId) {
      throw new Error("Collection response is missing the expected section");
    }
    this.#validateSectionContent(view);
    return { url, view };
  }

  #validateSectionContent(view) {
    if (!view.querySelector("[data-collection-status]")) {
      throw new Error("Collection response is incomplete");
    }
    const list = view.querySelector(".collection__main product-list");
    if (!list) {
      if (!view.querySelector(".empty-state p")?.textContent.trim()) {
        throw new Error("Collection response has no results or empty state");
      }
      return;
    }
    const pagination = view.querySelector("[data-collection-pagination]");
    const page = Number(pagination?.dataset.currentPage);
    const total = Number(pagination?.dataset.totalProducts);
    const shown = Number(pagination?.dataset.shownProducts);
    const cards = Array.from(list.children);
    if (list.id !== `product-list-${this.dataset.sectionId}` || !pagination ||
        !Number.isSafeInteger(page) || page < 1 || !Number.isSafeInteger(total) || total < 0 ||
        !Number.isSafeInteger(shown) || shown < 0 || shown > total || shown !== cards.length ||
        cards.some((card) => card.tagName !== "PRODUCT-CARD" || !card.getAttribute("handle"))) {
      throw new Error("Collection response has invalid results");
    }
  }

  #commitUrl(url) {
    const visibleUrl = new URL(url);
    visibleUrl.searchParams.delete("section_id");
    history.replaceState(history.state, "", visibleUrl);
  }

  async #loadMore(control) {
    const currentPagination = this.querySelector("[data-collection-pagination]");
    if (!currentPagination || !this.#productList) return;
    const currentPage = Number(currentPagination.dataset.currentPage);
    let nextUrl;
    try {
      nextUrl = this.#requestUrl(control.href);
    } catch (error) {
      this.#showRequestError();
      return;
    }
    const expectedPage = Number(nextUrl.searchParams.get("page"));
    if (!Number.isSafeInteger(currentPage) || !Number.isSafeInteger(expectedPage) || expectedPage !== currentPage + 1) {
      this.#showRequestError();
      return;
    }
    const request = this.#startRequest();
    try {
      const { view } = await this.#fetchSection(nextUrl, request);
      if (!this.#isCurrentRequest(request)) return;
      const returnedList = view.querySelector(".collection__main product-list");
      const returnedPagination = view.querySelector("[data-collection-pagination]");
      const returnedPage = Number(returnedPagination?.dataset.currentPage);
      const totalProducts = Number(returnedPagination?.dataset.totalProducts);
      if (!returnedList || returnedList.id !== this.#productList.id || !returnedPagination ||
          returnedPage !== expectedPage) {
        throw new Error("Collection page response is invalid");
      }
      const cards = Array.from(returnedList.children);
      const displayedCount = this.#productList.children.length + cards.length;
      if (displayedCount > totalProducts) {
        throw new Error("Collection page total does not match displayed products");
      }
      const handles = new Set(Array.from(this.#productList.children, (card) => card.getAttribute("handle")));
      if (cards.some((card) => {
        const handle = card.getAttribute("handle");
        if (handles.has(handle)) return true;
        handles.add(handle);
        return false;
      })) {
        throw new Error("Collection page contains already displayed products");
      }
      const pagination = document.importNode(returnedPagination, true);
      const shownCount = pagination.querySelector("[data-pagination-shown-count]");
      const progressBar = pagination.querySelector("[data-pagination-progress-bar]");
      if (!shownCount || !progressBar) {
        throw new Error("Collection page progress is missing");
      }
      pagination.dataset.shownProducts = String(displayedCount);
      shownCount.textContent = String(displayedCount);
      progressBar.style.setProperty("--collection-pagination-progress", `${totalProducts > 0 ? displayedCount * 100 / totalProducts : 0}%`);
      const newCards = cards.map((card) => document.importNode(card, true));
      newCards.forEach((card) => card.removeAttribute("reveal-on-scroll"));
      this.#productList.append(...newCards);
      currentPagination.replaceWith(pagination);
      this.#notifyMediaView();
    } catch (error) {
      if (this.#isCurrentRequest(request)) this.#showRequestError();
    } finally {
      this.#finishRequest(request);
    }
  }

  #showRequestError(message = this.dataset.requestError) {
    const status = this.querySelector("[data-collection-status]");
    if (!status || !this.isConnected) return;
    status.textContent = message;
    status.hidden = false;
  }

  // At most one cart preference write is in flight; intermediate clicks coalesce.
  async #persistLayout() {
    if (this.#writingLayout) return;
    this.#writingLayout = true;
    try {
      while (this.#queuedLayout && this.isConnected) {
        const layout = this.#queuedLayout;
        this.#queuedLayout = undefined;
        const controller = new AbortController();
        this.#layoutWriteController = controller;
        const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
          const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ attributes: { products_desktop_grid_mode: layout } }),
            credentials: "same-origin",
            signal: controller.signal,
            keepalive: true,
          });
          if (!response.ok) throw new Error("Collection layout preference was not saved");
          const cart = await response.json();
          if (cart?.attributes?.products_desktop_grid_mode !== layout) {
            throw new Error("Collection layout preference response does not match");
          }
          if (this.#queuedLayout === layout) this.#queuedLayout = undefined;
        } finally {
          clearTimeout(timeout);
        }
      }
    } catch (error) {
      // An ambiguous preference write is not retried automatically.
      this.#queuedLayout = undefined;
      if (this.isConnected) this.#showRequestError(this.dataset.layoutError);
    } finally {
      this.#layoutWriteController = undefined;
      this.#writingLayout = false;
    }
  }

  #applyDefaultSort() {
    if (this.dataset.designMode === "true" || this.dataset.useDefaultSort !== "true" || !this.dataset.defaultSort) return;
    const url = new URL(location.href);
    if (url.searchParams.has("sort_by")) return;
    url.searchParams.set("sort_by", this.dataset.defaultSort);
    location.replace(url);
  }

  #configureNavigation() {
    this.#navigationListeners?.abort();
    this.#navigationListeners = new AbortController();
    const { signal } = this.#navigationListeners;
    const grid = this.querySelector(".category-grid");
    const title = this.querySelector(".collection-toolbar__title, .collection-heading__title-two");
    if (!grid || !title) return;
    const left = this.querySelector(".scroll-left");
    const right = this.querySelector(".scroll-right");
    const hasLinks = grid.querySelectorAll("a.category-button").length > 0;
    const headingVisible = this.dataset.showCollectionTitle === "true";
    this.classList.toggle("has-category-links", hasLinks);
    if (!headingVisible) title.style.removeProperty("display");
    let lastScroll = window.scrollY;
    const updateButtons = () => {
      const desktop = window.innerWidth >= CATEGORY_DESKTOP_MIN_WIDTH;
      const gridVisible = !this.classList.contains("categories-hidden");
      left?.classList.toggle("is-visible", !desktop && gridVisible && grid.scrollLeft > 0);
      right?.classList.toggle("is-visible", !desktop && gridVisible && Math.ceil(grid.scrollLeft + grid.clientWidth) < grid.scrollWidth);
    };
    const updateScroll = () => {
      if (!headingVisible) {
        const y = window.scrollY;
        if (y > lastScroll && y > CATEGORY_HIDE_SCROLL_Y) this.classList.add("categories-hidden");
        else if (y < lastScroll && y < CATEGORY_HIDE_SCROLL_Y) this.classList.remove("categories-hidden");
        this.classList.toggle("toolbar-scrolled", !hasLinks && y > EMPTY_TOOLBAR_SCROLL_Y);
        lastScroll = y;
      }
      updateButtons();
    };
    const updateViewport = () => {
      if (window.innerWidth < CATEGORY_DESKTOP_MIN_WIDTH) this.classList.remove("categories-hidden");
      updateButtons();
      this.#syncControls();
    };
    grid.addEventListener("scroll", updateButtons, { signal, passive: true });
    window.addEventListener("scroll", updateScroll, { signal, passive: true });
    window.addEventListener("resize", updateViewport, { signal });
    const scrollCategories = (offset) => grid.scrollBy({
      left: offset,
      behavior: matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
    });
    left?.addEventListener("click", () => scrollCategories(-150), { signal });
    right?.addEventListener("click", () => scrollCategories(150), { signal });
    // Touch uses native horizontal scrolling. Mouse dragging stays scoped.
    let drag;
    let suppressClick = false;
    grid.addEventListener("pointerdown", (event) => {
      suppressClick = false;
      if (event.pointerType !== "mouse" || event.button !== 0 || window.innerWidth > CATEGORY_DESKTOP_MIN_WIDTH) return;
      drag = { pointerId: event.pointerId, x: event.clientX, scrollLeft: grid.scrollLeft, started: false };
    }, { signal });
    grid.addEventListener("pointermove", (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      const offset = event.clientX - drag.x;
      if (!drag.started) {
        if (Math.abs(offset) < CATEGORY_DRAG_THRESHOLD_PX) return;
        drag.started = true;
        grid.setPointerCapture(event.pointerId);
      }
      event.preventDefault();
      grid.scrollLeft = drag.scrollLeft - offset;
    }, { signal });
    const endDrag = (event) => {
      if (!drag || event.pointerId !== drag.pointerId) return;
      suppressClick = drag.started;
      drag = undefined;
      if (grid.hasPointerCapture(event.pointerId)) grid.releasePointerCapture(event.pointerId);
    };
    window.addEventListener("pointerup", endDrag, { signal });
    grid.addEventListener("pointercancel", endDrag, { signal });
    grid.addEventListener("lostpointercapture", endDrag, { signal });
    grid.addEventListener("dragstart", (event) => {
      if (drag) event.preventDefault();
    }, { signal });
    grid.addEventListener("click", (event) => {
      if (!suppressClick || event.detail === 0) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    }, { signal, capture: true });
    signal.addEventListener("abort", () => {
      if (drag && grid.hasPointerCapture(drag.pointerId)) grid.releasePointerCapture(drag.pointerId);
      drag = undefined;
      suppressClick = false;
    }, { once: true });
    updateViewport();
    updateScroll();
  }
}

if (!customElements.get("collection-view")) customElements.define("collection-view", CollectionView);
